import { useEffect, useState } from "react";
import { CheckSquare, Square, Plus, Trash2, Calendar as CalIcon } from "lucide-react";
import api, { fmtDateUS } from "@/lib/api";
import { toast } from "sonner";

/**
 * LeadTasks — quick checklist of todos on a single lead.
 *
 * Each add/complete writes through the backend, which also appends to the
 * lead's activity log (`task_added` / `task_completed`).
 */

export default function LeadTasks({ leadId, onChanged }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}/tasks`);
      setTasks(data);
    } catch (e) {
      // empty is fine
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [leadId]);

  const add = async () => {
    if (!title.trim()) return toast.error("Add a task title");
    setSubmitting(true);
    try {
      await api.post(`/leads/${leadId}/tasks`, {
        title: title.trim(),
        due_date: dueDate || null,
      });
      setTitle("");
      setDueDate("");
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add task");
    } finally {
      setSubmitting(false);
    }
  };

  const toggle = async (t) => {
    try {
      await api.patch(`/leads/${leadId}/tasks/${t.id}`, { completed: !t.completed });
      await load();
      if (onChanged && !t.completed) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update task");
    }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/leads/${leadId}/tasks/${t.id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete task");
    }
  };

  const open = tasks.filter((t) => !t.completed);
  const done = tasks.filter((t) => t.completed);

  return (
    <div data-testid="lead-tasks">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckSquare size={14} style={{ color: "var(--lime)" }} />
          <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text)" }}>
            Tasks {open.length > 0 ? `(${open.length})` : ""}
          </span>
        </div>
      </div>

      {/* Inline add form */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a task..."
          className="input-base text-sm flex-1"
          data-testid="task-title-input"
        />
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="input-base text-sm sm:w-44"
          title="Due date (optional)"
          data-testid="task-due-input"
        />
        <button
          onClick={add}
          disabled={submitting}
          className="text-xs font-semibold px-4 py-2 rounded-xl flex items-center gap-1.5 disabled:opacity-50 shrink-0"
          style={{ background: "var(--lime)", color: "#0c0c0c" }}
          data-testid="task-add"
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-4"><span className="spinner" /></div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-3 text-xs" style={{ color: "var(--text-dim)" }}>
          No tasks yet. Things like &quot;Email title co.&quot; or &quot;Send comps&quot; live here.
        </div>
      ) : (
        <>
          {open.length > 0 && (
            <ul className="space-y-1.5 mb-3">
              {open.map((t) => <TaskRow key={t.id} task={t} onToggle={toggle} onRemove={remove} />)}
            </ul>
          )}
          {done.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer font-mono uppercase tracking-wider mb-2" style={{ color: "var(--text-dim)" }}>
                {done.length} completed
              </summary>
              <ul className="space-y-1.5">
                {done.map((t) => <TaskRow key={t.id} task={t} onToggle={toggle} onRemove={remove} />)}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function TaskRow({ task, onToggle, onRemove }) {
  return (
    <li
      className="flex items-center gap-3 p-2.5 rounded-xl"
      style={{
        background: task.completed ? "transparent" : "var(--surface)",
        border: "1px solid var(--border)",
        opacity: task.completed ? 0.55 : 1,
      }}
      data-testid={`task-row-${task.id}`}
    >
      <button
        onClick={() => onToggle(task)}
        className="shrink-0"
        style={{ color: task.completed ? "var(--lime)" : "var(--text-muted)" }}
        data-testid={`task-toggle-${task.id}`}
      >
        {task.completed ? <CheckSquare size={16} /> : <Square size={16} />}
      </button>
      <div className="flex-1 min-w-0">
        <div
          className="text-sm truncate"
          style={{ color: task.completed ? "var(--text-muted)" : "var(--text)", textDecoration: task.completed ? "line-through" : "none" }}
        >
          {task.title}
        </div>
        {task.due_date && (
          <div className="flex items-center gap-1 text-[11px] mt-0.5" style={{ color: "var(--text-dim)" }}>
            <CalIcon size={10} /> Due {fmtDateUS(task.due_date)}
          </div>
        )}
      </div>
      <button
        onClick={() => onRemove(task)}
        className="p-1.5 rounded-lg hover:bg-white/5 transition shrink-0"
        style={{ color: "#f87171" }}
        title="Delete task"
        data-testid={`task-delete-${task.id}`}
      >
        <Trash2 size={11} />
      </button>
    </li>
  );
}
